//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { setRightsExpiryDate } from '@apis/rights-management/rightsManagementApi';
import { getMediaItemMetadata } from '@apis/universal-image-actions/universalImageActionsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { selectedItemActions } from '@uia/selected-item/selected-item-actions';

interface SetExpiryDateMutationParams {
  cuid: Cuid;
  expirationTime?: Date;
}

export const useSetExpiryDateMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cuid,
      expirationTime,
    }: SetExpiryDateMutationParams) => {
      await setRightsExpiryDate({
        cuid,
        expirationTime,
      });

      return getMediaItemMetadata(cuid);
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message:
            'Unable to update rights expiration date. Please try again or contact Bazaarvoice Support.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
          title: 'Failed to Update Expiry Date',
          type: ToastType.ERROR,
        }),
      );
    },
    onSettled: async (metadata) => {
      if (metadata) {
        dispatch(selectedItemActions.receiveItemMetadata(metadata));
      }
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.UniversalImageActions,
          QueryKeyConstants.UniversalImageActionsRights,
          QueryKeyConstants.UniversalImageActionsMetadata,
        ],
      });
    },
  });
};
